/**
 * VM-11b — Kündigung ändern (nachträglich).
 *
 * Erlaubt: Frist verkürzen (auch rückdatierend), letzten Monat anteilig
 * berechnen, Eingangsmetadaten korrigieren. Sollstellungen werden serverseitig
 * neu bereinigt und ggf. anteilig gekürzt.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Warning } from "@phosphor-icons/react";

export default function TerminationEditDialog({ open, onOpenChange, tenancy, onDone }) {
  const [effectiveDate, setEffectiveDate] = useState("");
  const [prorate, setProrate] = useState(false);
  const [receivedAt, setReceivedAt] = useState("");
  const [receivedBy, setReceivedBy] = useState("manager");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && tenancy) {
      setEffectiveDate(tenancy.termination_effective_date || tenancy.contract_end || tenancy.termination_date || "");
      setProrate(!!tenancy.termination_prorate_last_month);
      setReceivedAt(tenancy.termination_received_at || "");
      setReceivedBy(tenancy.termination_received_by || "manager");
      setReason(tenancy.termination_reason || "");
      setBusy(false);
    }
  }, [open, tenancy]);

  const originalEnd = tenancy?.termination_effective_date;
  const today = new Date().toISOString().slice(0, 10);
  const isRetroactive = effectiveDate && effectiveDate < today;
  const isShortening = originalEnd && effectiveDate && effectiveDate < originalEnd;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/tenancies/${tenancy.id}/termination`, {
        effective_date: effectiveDate || null,
        prorate_last_month: prorate,
        received_at: receivedAt || null,
        received_by: receivedBy || null,
        reason: reason || null,
      });
      let msg = "Kündigung aktualisiert";
      if (data.deleted_future_postings) msg += ` · ${data.deleted_future_postings} Sollstellung(en) gelöscht`;
      if (data.prorated_adjusted_count) msg += ` · ${data.prorated_adjusted_count} Sollstellung(en) anteilig gekürzt`;
      toast.success(msg);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Aktualisieren");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="terminate-edit-dialog">
        <DialogHeader><DialogTitle>Kündigung ändern</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Neues Vertragsende</Label>
            <Input
              type="date" value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
              data-testid="terminate-edit-effective-date"
            />
            {isShortening && (
              <div className="text-[11px] text-amber-700 mt-1">
                ⚠ Frist verkürzt (regulär: {new Date(originalEnd).toLocaleDateString("de-DE")})
              </div>
            )}
            {isRetroactive && (
              <div className="mt-2 flex items-start gap-2 text-[11px] text-red-800 bg-red-50 border border-red-200 rounded-md px-2 py-1.5">
                <Warning size={14} className="mt-0.5 shrink-0" />
                <span>
                  <strong>Rückdatierung:</strong> Bereits gebuchte Sollstellungen ab dem neuen Endedatum werden
                  gelöscht bzw. gekürzt. Zahlungseingänge bleiben unverändert — sie erscheinen dann als
                  Überzahlung im Mietkonto.
                </span>
              </div>
            )}
          </div>

          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input
              type="checkbox" className="h-4 w-4 accent-slate-900 mt-0.5"
              checked={prorate} onChange={(e) => setProrate(e.target.checked)}
              data-testid="terminate-edit-prorate"
            />
            <span>
              <strong>Letzten Monat anteilig berechnen</strong>
              <span className="block text-[11px] text-slate-500 mt-0.5">
                Sollstellung im Endmonat wird auf <code>Warmmiete / 30 × Tage_bis_Ende</code> gekürzt.
              </span>
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Eingegangen am</Label>
              <Input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Eingegangen bei</Label>
              <select
                className="mt-1 w-full h-9 rounded-md border border-slate-300 bg-white px-3 text-sm"
                value={receivedBy}
                onChange={(e) => setReceivedBy(e.target.value)}
              >
                <option value="manager">Hausverwaltung</option>
                <option value="owner">Eigentümer</option>
              </select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Notiz / Grund</Label>
            <textarea
              className="mt-1 w-full min-h-[50px] rounded-md border border-slate-300 bg-white px-3 py-2 text-sm"
              value={reason} onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} data-testid="terminate-edit-submit">
            {busy ? "Wird gespeichert…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
